<script setup>
/**
 * The ✎ menu on a reading (a day's moment, a thread's step): "Add my hunch",
 * "Add what I took from it", only the ones not written yet (journal threads
 * left, 2026-10-28). The page never shows an empty label; this is how a line
 * is added later. Never on a reading done for someone (the parent passes none).
 */
import { ref, nextTick, onBeforeUnmount } from 'vue'
import { OWN_ADD } from '@/utils/journal-practice'

defineProps({
  pieces: { type: Array, default: () => [] },   // 'gut' | 'landed'
  testid: { type: String, default: 'journal-add-own' },
})
const emit = defineEmits(['add'])
const open = ref(false)
const popEl = ref(null)
const shift = ref(0)   // px the menu moves left to stay on the page
async function place () {
  shift.value = 0
  await nextTick()
  const el = popEl.value
  if (!el || !el.getBoundingClientRect) return
  const r = el.getBoundingClientRect()
  const page = el.closest('.page') || document.documentElement
  const right = page.getBoundingClientRect ? page.getBoundingClientRect().right : window.innerWidth
  const edge = Math.min(right || window.innerWidth, window.innerWidth) - 10
  if (r.right > edge) shift.value = Math.min(r.right - edge, Math.max(0, r.left - 10))
}
function away () { open.value = false; document.removeEventListener('click', away) }
function toggle () {
  open.value = !open.value
  if (open.value) { place(); setTimeout(() => document.addEventListener('click', away), 0) }
  else document.removeEventListener('click', away)
}
function pick (p) { away(); emit('add', p) }
onBeforeUnmount(() => document.removeEventListener('click', away))
</script>

<template>
  <span v-if="pieces.length" class="addown" @click.stop>
    <button type="button" class="pen" :aria-expanded="open" aria-label="Add your words" :data-testid="testid" @click="toggle">✎</button>
    <span v-if="open" ref="popEl" class="pop" role="menu" :style="shift ? { transform: `translateX(-${shift}px)` } : null">
      <button v-for="p in pieces" :key="p" type="button" role="menuitem" :data-testid="`${testid}-${p}`" @click="pick(p)">{{ OWN_ADD[p] }}</button>
    </span>
  </span>
</template>

<style scoped>
.addown { position: relative; display: inline-block; vertical-align: middle; }
.pen { border: 0; background: none; cursor: pointer; padding: 4px 8px; border-radius: 6px; font: 600 13px var(--j-ui); color: var(--j-hand); opacity: .7; }
.pen:hover, .pen:focus-visible { opacity: 1; background: rgba(201, 169, 78, .14); }
.pop { position: absolute; z-index: 9; left: 0; top: 100%; min-width: 210px; padding: 6px; border-radius: 10px; background: #fbf6ea; box-shadow: 0 10px 30px rgba(42, 24, 69, .3), 0 0 0 1px rgba(42, 24, 69, .1); }
.pop button { display: block; width: 100%; text-align: left; padding: 10px; border-radius: 7px; font: 500 14px var(--j-ui); color: var(--j-ink); background: none; border: 0; cursor: pointer; white-space: nowrap; }
.pop button:hover { background: rgba(42, 24, 69, .07); }
</style>
